import { getUnitLabelForUnitSystem } from '@icommerce/units';
import { Button } from '@common/Button/Button';
import { IconID } from '@common/Icon/Icon';
import { Modal, ModalVariant } from '@common/Modal/Modal';
import { KetteEinbauArt } from '@hooks/states/echainConfigSlice';
import { useEchainProductStore } from '@hooks/states/useEchainProductStore';
import { useProductPageStore } from '@hooks/states/useProductPageStore';
import type { ProductAttribute } from '@libs/attribute.helper';
import { getAttributeValue } from '@libs/attribute.helper';
import { getLocale } from '@libs/constants';
import { getInteriorSeparationAssociationProducts } from '@libs/echain';
import { WidenUtil } from '@libs/widen-utils';
import type { AssociationProductFragmentEnhanced, ModelAssociationFragmentEnhanced } from '@models/enhanced-fragments';
import { WidenImageConfig } from '@models/widen-image-config';
import { CollapsibleBackgroundTheme, CollapsibleTheme } from '@product/Collapsible/BaseCollapsible';
import { Collapsible } from '@product/Collapsible/Collapsible';
import { GuideThroughConfiguratorBox } from '@product/GuideThroughConfiguratorBox/GuideThroughConfiguratorBox';
import { InteriorSeparationConfiguratorBox } from '@product/InteriorSeparationConfiguratorBox/InteriorSeparationConfiguratorBox';
import { useShelvesProductAssociationModelsQuery } from '@product/ProductPage/PageLayouts/hooks/useShelvesProductAssociationModelsQuery';
import { useUniqueAssociatedProductsQuery } from '@product/ProductPage/PageLayouts/hooks/useUniqueAssociatedProductsQuery';
import { PRODUCT_DETAIL_COLLAPSIBLE_KEY } from '@product/ProductTabs/ProductTabsTypes';
import { MountingBracketSelector } from '@product/VariantSelector/MountingBracketSelector/MountingBracketSelector';
import { SeparatorsSelector } from '@product/VariantSelector/SeparatorsSelector/SeparatorsSelector';
import { ShelvesSelector } from '@product/VariantSelector/ShelvesSelector/ShelvesSelector';
import { SupportTraySelector } from '@product/VariantSelector/SupportTraySelector/SupportTraySelector';
import {
  getReadonlyVariantAxisItemValueString,
  getSelectedItemForVariantAxis,
  getVariantAxisItemValueString,
} from '@product/VariantSelector/VariantSelector';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useState } from 'react';
import { InstallationTypeSelection } from './InstallationTypeSelection/InstallationTypeSelection';

export interface EinbauartProperties {
  type: KetteEinbauArt;
  available: boolean;
  title: string;
  iconUrl: string;
  disabled?: boolean;
}

export const ProductSelectionTab: React.FC = () => {
  const { t } = useTranslation(['product/echainBuyBox', 'product/productDetails']);
  const unitSystem = useProductPageStore(state => state.unitSystem);
  const router = useRouter();
  const locale = getLocale(router);
  const allProducts = useProductPageStore(state => state.allProducts);
  const setSelectedVariantSku = useProductPageStore(state => state.setSelectedVariantSku);
  const currentProduct = useProductPageStore(state => state.currentProduct);
  const availableVariantAxes = useProductPageStore(state => state.availableVariantAxes);
  const setEinbauart = useEchainProductStore(state => state.setEinbauart);
  const setKetteFromProduct = useEchainProductStore(state => state.setKetteFromProduct);
  const einbauArt = useEchainProductStore(state => state.einbauArt);
  const kette = useEchainProductStore(state => state.kette);
  const groupedMountingBrackets = useEchainProductStore(state => state.groupedMountingBrackets);
  const groupedSupportTrays = useEchainProductStore(state => state.groupedSupportTrays);
  const [storedUniqueAssociatedProducts, setStoredUniqueAssociatedProducts] = useState(
    {} as Record<string, AssociationProductFragmentEnhanced> | undefined
  );
  const [storedShelvesProductAssociationModelsResult, setStoredShelvesProductAssociationModelsResult] = useState(
    [] as ModelAssociationFragmentEnhanced[] | undefined
  );
  const ketteConfigurationFailed = useEchainProductStore(state => state.ketteConfigurationFailed);
  const setKetteConfigurationFailed = useEchainProductStore(state => state.setKetteConfigurationFailed);

  useEffect(() => {
    // reset the selection in case of an error
    if (ketteConfigurationFailed) {
      // get default sku
      const sku = allProducts[0].id;
      setEinbauart(KetteEinbauArt.freitragend);
      setSelectedVariantSku(sku);
    }
  }, [allProducts, ketteConfigurationFailed, setSelectedVariantSku, setEinbauart]);

  // when the attribute is not set on the product, assume einbauartGleitend can be selected.
  const canSelectEinbauartGleitend =
    currentProduct.attributes?.attr_gliding_bool === undefined
      ? true
      : getAttributeValue<boolean>(currentProduct.attributes?.attr_gliding_bool as ProductAttribute<boolean>);

  const { data: uniqueAssociatedProducts, isLoading: isLoadingUniqueAssociatedProducts } =
    useUniqueAssociatedProductsQuery();

  // cache the previous result of the uniqueAssociatedProductsQuery and only reset it when loading is finished
  // this way we avoid that the whole section will disappear and then reappear now that we load the contents of it dynamically on demand
  useEffect(() => {
    if (!isLoadingUniqueAssociatedProducts) {
      setStoredUniqueAssociatedProducts(uniqueAssociatedProducts);
    }
  }, [uniqueAssociatedProducts, isLoadingUniqueAssociatedProducts]);

  const { data: shelvesProductAssociationModelsResult, isLoading: isLoadingShelvesProductAssociationModels } =
    useShelvesProductAssociationModelsQuery();

  // cache the previous result of the shelvesProductAssociationModelsQuery and only reset it when loading is finished
  // this way we avoid that the whole section will disappear and then reappear now that we load the contents of it dynamically on demand
  useEffect(() => {
    if (!isLoadingShelvesProductAssociationModels) {
      setStoredShelvesProductAssociationModelsResult(shelvesProductAssociationModelsResult);
    }
  }, [shelvesProductAssociationModelsResult, isLoadingShelvesProductAssociationModels]);

  const shelvesAssociationModelsFiltered = useMemo(
    () =>
      storedShelvesProductAssociationModelsResult?.filter(model =>
        model.products.some(
          product => !getAttributeValue<boolean>(product?.attributes?.mounted_bool as ProductAttribute<boolean>)
        )
      ),
    [storedShelvesProductAssociationModelsResult]
  );

  const separatorsAssociationProducts = useMemo(
    () =>
      storedUniqueAssociatedProducts
        ? getInteriorSeparationAssociationProducts(currentProduct, storedUniqueAssociatedProducts)
        : undefined,
    [currentProduct, storedUniqueAssociatedProducts]
  );

  const einbauarten: EinbauartProperties[] = [
    {
      type: KetteEinbauArt.freitragend,
      available: !!kette.FreitragenderBetriebErlaubt,
      title: t('ECHAIN_PRODUCT_PAGE.ECHAIN_VARIANTS.ECHAIN_UNSUPPORTED'),
      iconUrl: WidenUtil.getWidenCDNUrl(
        new WidenImageConfig('0kmwfjwa1o', 'ECS_PICTO_installation_self-supporting_1.webp', 300, 300)
      ),
    },
    {
      type: KetteEinbauArt.gleitend,
      available: !!kette.GleitenderBetriebErlaubt,
      title: t('ECHAIN_PRODUCT_PAGE.ECHAIN_VARIANTS.ECHAIN_GLIDING'),
      iconUrl: WidenUtil.getWidenCDNUrl(
        new WidenImageConfig('e5ebtku434', 'ECS_PICTO_installation_gliding_1.webp', 300, 300)
      ),
      disabled: !canSelectEinbauartGleitend,
    },
    {
      type: KetteEinbauArt.haengend,
      available: !!kette.FreitragenderBetriebErlaubt,
      title: t('ECHAIN_PRODUCT_PAGE.ECHAIN_VARIANTS.ECHAIN_HANGING'),
      iconUrl: WidenUtil.getWidenCDNUrl(
        new WidenImageConfig('3xtp7y2yto', 'ECS_PICTO_installation_hanging_1.webp', 300, 300)
      ),
    },
  ];

  // add selected configurations to parts list
  useEffect(() => {
    setKetteFromProduct(currentProduct);
  }, [currentProduct, setKetteFromProduct]);

  const productSelectionContentSections = [
    {
      key: PRODUCT_DETAIL_COLLAPSIBLE_KEY.E_CHAIN_INSTALLATION_TYPE,
      title: `${t('ECHAIN_PRODUCT_PAGE.SECTION_TITLES.INSTALLATION_TYPE_SECTION_HEADER')}:`,
      titleIcon: IconID.ECHAIN_SELECTION,
      content: (
        <InstallationTypeSelection
          onSelect={setEinbauart}
          einbauArt={einbauArt}
          einbauarten={einbauarten}
          locale={locale}
        />
      ),
      alternativeContent: (
        <div className={classNames('-mt-8', 'px-6', 'pb-8', 'text-gray-300')}>
          <div className={classNames('inline')}>
            <strong>{t('ECHAIN_PRODUCT_PAGE.SECTION_TITLES.INSTALLATION_TYPE_TITLE')}: </strong>
            <span>
              {einbauarten.find(einbauartProps => einbauartProps.type === einbauArt)?.title ?? einbauarten[0].title}
            </span>
          </div>
          <div className={classNames('inline')}>
            <span className={classNames('my-6')}> | </span>
            <strong>{t('PRODUCT_DETAILS.PRODUCT_NO', { ns: 'product/productDetails' })}: </strong>
            <span>{currentProduct.id}</span>
          </div>
          {availableVariantAxes?.map(variantAxis => {
            const selectedItem = getSelectedItemForVariantAxis(variantAxis, currentProduct);
            let value = '';
            if (variantAxis.readonly && selectedItem) {
              value = getReadonlyVariantAxisItemValueString(variantAxis, selectedItem, locale, unitSystem);
            }
            if (variantAxis.items.length > 0) {
              value = getVariantAxisItemValueString(selectedItem);
            }
            return (
              <div className={classNames('inline')} key={`variantAxis-${variantAxis.attribute}-selected-value-preview`}>
                <span className={classNames('my-6')}> | </span>
                <strong>
                  {variantAxis.label}
                  {typeof selectedItem !== 'string' && typeof selectedItem !== 'number' && selectedItem
                    ? ` [${getUnitLabelForUnitSystem(selectedItem.unit, unitSystem)}]`
                    : ''}
                  :{' '}
                </strong>
                <span>{value}</span>
              </div>
            );
          })}
        </div>
      ),
      openPerDefault: true,
    },
    ...(groupedMountingBrackets && groupedMountingBrackets.length > 0
      ? [
          {
            key: PRODUCT_DETAIL_COLLAPSIBLE_KEY.E_CHAIN_MOUNTING_BRACKETS,
            title: `${t('ECHAIN_PRODUCT_PAGE.SECTION_TITLES.MOUNTING_BRACKETS_TITLE')}:`,
            titleIcon: IconID.ECHAIN_AE_SELECTION,
            content: (
              <div className={classNames('px-6', 'py-8')}>
                <MountingBracketSelector />
              </div>
            ),
            openPerDefault: false,
          },
        ]
      : []),
    ...((separatorsAssociationProducts && separatorsAssociationProducts.length > 0) ||
    (shelvesAssociationModelsFiltered && shelvesAssociationModelsFiltered.length > 0)
      ? [
          {
            key: PRODUCT_DETAIL_COLLAPSIBLE_KEY.E_CHAIN_INTERIOR_SEPARATION,
            title: `${t('ECHAIN_PRODUCT_PAGE.SECTION_TITLES.INTERIOR_SEPARATION_TITLE')}:`,
            titleIcon: IconID.ECHAIN_IA_SELECTION,
            content: (
              <div className="flex flex-col px-6 pt-4 pb-8" data-testid="interiorSeparationSelectorBox">
                <InteriorSeparationConfiguratorBox currentProduct={currentProduct} />
                {separatorsAssociationProducts && separatorsAssociationProducts.length > 0 && (
                  <div className={classNames('mb-3')}>
                    <SeparatorsSelector separatorsAssociationProducts={separatorsAssociationProducts} />
                  </div>
                )}
                {shelvesAssociationModelsFiltered && shelvesAssociationModelsFiltered.length > 0 && (
                  <div>
                    <ShelvesSelector shelvesAssociationModels={shelvesAssociationModelsFiltered} />
                  </div>
                )}
              </div>
            ),
            openPerDefault: false,
          },
        ]
      : []),
    ...(einbauArt === KetteEinbauArt.freitragend && groupedSupportTrays && groupedSupportTrays.length > 0
      ? [
          {
            key: PRODUCT_DETAIL_COLLAPSIBLE_KEY.E_CHAIN_SUPPORT_TRAYS,
            title: `${t('ECHAIN_PRODUCT_PAGE.SECTION_TITLES.SUPPORT_TRAYS_TITLE')}:`,
            titleIcon: IconID.ECHAIN_TRAY_SELECTION,
            content: (
              <div data-testid="supportTraySelectorBox" className={classNames('px-6', 'py-8')}>
                <SupportTraySelector />
              </div>
            ),
            openPerDefault: false,
          },
        ]
      : []),
    ...(einbauArt === KetteEinbauArt.gleitend
      ? [
          {
            key: PRODUCT_DETAIL_COLLAPSIBLE_KEY.E_CHAIN_GUIDE_TROUGH,
            titleIcon: IconID.ECHAIN_TRAY_SELECTION,
            title: `${t('ECHAIN_PRODUCT_PAGE.SECTION_TITLES.GUIDE_TROUGH_TITLE')}:`,
            content: (
              <div data-testid="guideThroughSelectorBox" className={classNames('px-6', 'py-8')}>
                <GuideThroughConfiguratorBox />
              </div>
            ),
            openPerDefault: false,
          },
        ]
      : []),
  ];

  return (
    <>
      {/* each productSelectionContentSection needs to be in their own collapsible, because they can be opened/closed independent of each other */}
      {productSelectionContentSections.map(productSelectionContentSection => (
        <div
          key={productSelectionContentSection.key}
          data-testid={`product-selection-collapsible-${productSelectionContentSection.key}`}
        >
          <Collapsible
            key={productSelectionContentSection.key}
            items={[productSelectionContentSection]}
            activeTab={productSelectionContentSection.openPerDefault ? 0 : -1}
            theme={CollapsibleTheme.LIGHT}
            backgroundTheme={CollapsibleBackgroundTheme.LIGHT}
            openIcon={IconID.DROPDOWN_UP}
            closeIcon={IconID.DROPDOWN_DOWN}
            titleIcon={productSelectionContentSection.titleIcon}
            disableScrolling
          />
        </div>
      ))}

      <Modal
        variant={ModalVariant.ALERT}
        visible={Boolean(ketteConfigurationFailed)}
        onClose={() => setKetteConfigurationFailed(false)}
        headline={t('ECHAIN_PRODUCT_PAGE.MODAL_UNEXPECTED_ERROR.TITLE')}
      >
        <p
          className="mb-12 text-base"
          dangerouslySetInnerHTML={{ __html: t('ECHAIN_PRODUCT_PAGE.MODAL_UNEXPECTED_ERROR.TEXT') }}
        />

        <div className="flex justify-between gap-3.5">
          <Button
            text={t('ECHAIN_PRODUCT_PAGE.MODAL_UNEXPECTED_ERROR.CONFIRMATION_BUTTON')}
            className="flex-1"
            onClick={() => setKetteConfigurationFailed(false)}
          />
        </div>
      </Modal>
    </>
  );
};
